import { useErrorReporter } from '@/composables/useErrorReporter'
import type {
	DrawCreateEvent, DrawDeleteEvent, DrawUpdateEvent
} from '@mapbox/mapbox-gl-draw'
import MapboxDraw from '@mapbox/mapbox-gl-draw'
import * as turf from '@turf/turf'
import type {
	Feature, GeoJsonProperties, Geometry, Polygon, Position
} from 'geojson'
import type { MapboxFeatureConfig } from './types'
import { DEFAULT_FEATURE_CONFIG } from './types'
import { useMapboxState } from './useMapboxState'

export function useMapboxDrawing(
	mapId: string,
	config: MapboxFeatureConfig = DEFAULT_FEATURE_CONFIG
) {
	const { bugsnagReport } = useErrorReporter()
	const {
		mapState, handleDrawCreate, handleDrawUpdate, setLassoFeatures, clearLassoFeatures, removeLayerFilter
	} = useMapboxState(mapId)

	const drawRef = ref<MapboxDraw | null>(null)
	const roundedArea = ref<number | null>(null)
	const lassoPolygon = ref<Feature<Polygon> | null>(null)

	// Initialize draw
	const initDraw = () => {
		const map = mapState.value.mapInstance
		if (!map) {
			return
		}

		drawRef.value = new MapboxDraw({
			displayControlsDefault: false,
			controls: {
				polygon: true,
				trash: true
			},
			defaultMode: 'simple_select'
		})

		map.addControl(drawRef.value as unknown as mapboxgl.IControl, 'top-right')

		// Set up event handlers
		map.on('draw.create', onDrawCreate)
		map.on('draw.delete', onDrawDelete)
		map.on('draw.update', onDrawUpdate)
		map.on('draw.modechange', onDrawModeChange)
	}

	// Mode change handler
	const onDrawModeChange = () => {
		const map = mapState.value.mapInstance
		if (!map || !drawRef.value) {
			return
		}

		const mode = drawRef.value.getMode()
		if (mode === 'draw_polygon') {
			map.getCanvas().style.cursor = 'crosshair'
		} else {
			map.getCanvas().style.cursor = ''
		}
	}

	// Clear the lasso polygon
	const clearLassoPolygon = () => {
		lassoPolygon.value = null
		roundedArea.value = null
		clearLassoFeatures()

		// Clear any drawn polygons if draw is initialized
		if (drawRef.value) {
			drawRef.value.deleteAll()
			const map = mapState.value.mapInstance
			if (!map || !drawRef.value) return

			// Use injected config for table data marker filter ID
			const tableFilterId = config.specialFilterIds?.tableDataMarkerId
			if (tableFilterId && map.getFilter(tableFilterId)) {
				removeLayerFilter(tableFilterId)
			}
		}
	}

	// Update bounds from draw
	const updateBounds = () => {
		if (!drawRef.value) return

		const data = drawRef.value.getAll()
		const newPolygon = data?.features?.[data.features.length - 1] as Feature<Polygon>
		lassoPolygon.value = newPolygon
	}

	// Draw create handler
	const onDrawCreate = (e: DrawCreateEvent) => {
		updateBounds()
		calculateArea()

		if (drawRef.value && lassoPolygon.value) {
			drawRef.value.deleteAll() // Clear previous polygons
			// Add the new polygon to the draw instance
			drawRef.value.add(lassoPolygon.value as Feature<Polygon>)
		}

		handleDrawCreate(lassoPolygon.value, e)
	}

	// Draw update handler
	const onDrawUpdate = (e: DrawUpdateEvent) => {
		calculateArea(e)
	}

	// Draw delete handler
	const onDrawDelete = (e: DrawDeleteEvent) => {
		const map = mapState.value.mapInstance
		if (!map || !drawRef.value) return

		calculateArea()

		const currentFeatures = Array.from(drawRef.value.getAll()?.features || [])
		if (!currentFeatures.length) {
			clearLassoPolygon()
		}

		handleDrawUpdate(lassoPolygon.value, roundedArea.value, [], e)
	}

	// Calculate area of polygon and find features inside it
	const calculateArea = (e?: DrawUpdateEvent) => {
		const map = mapState.value.mapInstance
		if (!map || !lassoPolygon.value) {
			roundedArea.value = null
			return
		}

		// Calculate area
		const area = turf.area(lassoPolygon.value)
		roundedArea.value = Math.round(area * 100) / 100

		// Update the polygon bounds
		updateBounds()

		// Find all visible features that are points
		const featuresInLassoPolygon: Feature<Geometry, GeoJsonProperties>[] = []

		// Get all the source IDs from the map style
		const style = map.getStyle()
		if (!style || !style.sources) return

		const sourceIds = Object.keys(style.sources)

		// Query each source for features
		sourceIds.forEach(sourceId => {
			// Skip raster, video, and image sources
			const source = style.sources[sourceId]
			if (source.type === 'raster' || source.type === 'video' || source.type === 'image') {
				return
			}

			try {
				// Query features from the current source with the active filters
				const layers = style.layers?.filter(layer => layer.source === sourceId)
				const currentFilteredLayer = layers?.find(layer => layer.filter)
				const sourceFeatures = map.querySourceFeatures(sourceId, {
					sourceLayer: sourceId, // Required for vector tiles
					filter: currentFilteredLayer?.filter || [
						'all',
						[ '==', [ 'geometry-type' ], 'Point' ] // default to all Point features
					]
				})

				// Skip if no features found
				if (!sourceFeatures || sourceFeatures.length === 0 || !lassoPolygon.value) {
					return
				}

				// Check each feature to see if it's within the polygon
				sourceFeatures.forEach(feature => {
					// Skip features without geometry
					if (!feature.geometry) {
						return
					}

					// For point features
					if (feature.geometry.type === 'Point') {
						const point = turf.point(feature.geometry.coordinates as [number, number])
						const isInside = turf.booleanPointInPolygon(point, lassoPolygon.value as Feature<Polygon>)

						if (isInside) {
							featuresInLassoPolygon.push(feature)
						}
					}
					// For other geometry types (lines, polygons), we could use turf.booleanContains
					// or other appropriate spatial predicates
				})
			} catch (error) {
				bugsnagReport({
					error: error instanceof Error ? error : new Error('Failed to query featues.'),
					showToast: true,
					toast: {
						notificationType: 'error',
						title: 'Failed to query featues.'
					}
				})
			}
		})

		// Deduplicate features by ID property from config
		const seenIds = new Set()
		const uniqueFeatures = featuresInLassoPolygon.filter(f => {
			const id = f.properties?.[config.idProperty]
			if (!id || seenIds.has(id)) {
				return false
			}
			seenIds.add(id)
			return true
		})

		// Update the state with the unique features found
		setLassoFeatures(uniqueFeatures)
		handleDrawUpdate(lassoPolygon.value, roundedArea.value, uniqueFeatures, e || {} as DrawUpdateEvent)

		// Update the last drawn polygon in the map state
		// This is used to export the polygon later
		mapState.value.lastDrawPolygon = lassoPolygon.value // TODO: Remove this since it's now handled in handleDrawUpdate?
	}

	// Export GeoJSON data
	const exportGeoJson = () => {
		if (!mapState.value.lastDrawPolygon || !mapState.value.lassoFeatures.length) return

		const sanitizeFeatureCoordinates = (
			coordinates: Position | Position[] | Position[][] | Position[][][]
		): Position | Position[] | Position[][] => {
			if (Array.isArray(coordinates)) {
				return coordinates.map(coord =>
					Array.isArray(coord) ? sanitizeFeatureCoordinates(coord) : coord
				) as Position | Position[]
			} else {
				return coordinates
			}
		}

		const mapGeometry = (feature: Feature<Geometry>): Geometry => {
			const geometry = feature?.geometry
			if (geometry.type === 'Polygon') {
				return {
					type: 'Polygon',
					coordinates: sanitizeFeatureCoordinates(geometry.coordinates) as Position[][]
				}
			} else if (geometry.type === 'MultiPolygon') {
				return {
					type: 'MultiPolygon',
					coordinates: (geometry.coordinates).map(polygon =>
						sanitizeFeatureCoordinates(polygon)
					) as Position[][][]
				}
			} else if (geometry.type === 'Point') {
				return {
					type: 'Point',
					coordinates: sanitizeFeatureCoordinates(geometry.coordinates) as Position
				}
			} else if (geometry.type === 'LineString') {
				return {
					type: 'LineString',
					coordinates: sanitizeFeatureCoordinates(geometry.coordinates) as Position[]
				}
			} else {
				return geometry
			}
		}

		const mappedFeatures = mapState.value.lassoFeatures.map(feature => ({
			properties: { [config.idProperty]: feature?.properties?.[config.idProperty] },
			geometry: mapGeometry(feature),
			type: feature?.type
		}))

		const jsonStr = encodeURIComponent(JSON.stringify({
			type: 'FeatureCollection',
			features: [ ...mappedFeatures, mapState.value.lastDrawPolygon ]
		}))

		const dataStr = 'data:text/json;charset=utf-8,' + jsonStr
		const downloadAnchorNode = document.createElement('a')
		const fileName = new Date().toISOString().replace(/:/g, '-') + '-map-features-export.geojson'
		downloadAnchorNode.setAttribute('href', dataStr)
		downloadAnchorNode.setAttribute('download', fileName)
		document.body.appendChild(downloadAnchorNode) // required for firefox
		downloadAnchorNode.click()
		downloadAnchorNode.remove()

		return fileName
	}

	return {
		drawRef,
		roundedArea,
		lassoPolygon,
		initDraw,
		clearLassoPolygon,
		exportGeoJson
	}
}
